import {Component, computed, effect, inject, input, output} from '@angular/core';
import {FormControl} from '@angular/forms';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom, startWith, switchMap} from 'rxjs';
import {PaymentMethod, PaymentModeService} from '../../../../../api';
import {QueryKey} from '../../../../constants/query-key';
import {Dropdown} from '../../../../components/dropdown/dropdown';

interface PaymentModeOption {
  id: number;
  name: string;
  method: PaymentMethod;
}

@Component({
  selector: 'elcia-payment-mode-dropdown',
  imports: [Dropdown, TranslatePipe],
  templateUrl: './payment-mode-dropdown.html',
  styleUrl: './payment-mode-dropdown.scss',
})
export class PaymentModeDropdown {
  private readonly paymentModeService = inject(PaymentModeService);
  private readonly translate = inject(TranslateService);
  private readonly langChange = toSignal(this.translate.onLangChange, {initialValue: null});

  readonly control = input.required<FormControl>();
  readonly methodChange = output<PaymentMethod | null>();

  private readonly paymentModesQuery = injectQuery(() => ({
    queryKey: [QueryKey.PAYMENT_MODES],
    queryFn: () => lastValueFrom(this.paymentModeService.getPaymentModes()),
  }));

  // Localized label comes from i18n keyed on the method, options keep the method for direct-debit detection
  protected readonly paymentModes = computed<PaymentModeOption[]>(() => {
    this.langChange();
    return (this.paymentModesQuery.data()?.paymentModes ?? []).map((mode) => ({
      id: mode.id,
      method: mode.method,
      name: this.translate.instant(`payment-method.${mode.method}`),
    }));
  });

  // Track the bound control value reactively so the method is emitted on patch (edit) and on user change
  private readonly selectedId = toSignal(
    toObservable(this.control).pipe(
      switchMap((control) => control.valueChanges.pipe(startWith(control.value))),
    ),
  );

  // Covers the edit case: emit the method once the patched value and options are both available
  private readonly emitSelectedMethod = effect(() => {
    const id = this.selectedId();
    this.methodChange.emit(this.methodFor(id));
  });

  // Covers user selection synchronously, independent of effect timing
  protected onSelectionChange(id: unknown): void {
    this.methodChange.emit(this.methodFor(id));
  }

  private methodFor(id: unknown): PaymentMethod | null {
    return this.paymentModes().find((mode) => mode.id === id)?.method ?? null;
  }
}
